import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { AlertTriangle, CheckCircle2, Info, OctagonAlert, X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { IconButton } from '@/shared/ui/core/IconButton';

export type ToastTone = 'success' | 'error' | 'info' | 'warning';

export type ToastInput = {
  title?: string;
  message: string;
  tone?: ToastTone;
  duration?: number;
};

type ToastItem = ToastInput & { id: string; open: boolean };

type ToastContextValue = {
  toast: (input: ToastInput) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const toneClass: Record<ToastTone, string> = {
  success: 'border-success bg-success-soft text-success-fg',
  error: 'border-danger bg-danger-soft text-danger-fg',
  info: 'border-info bg-info-soft text-info-fg',
  warning: 'border-warning bg-warning-soft text-warning-fg',
};

const toneLabel: Record<ToastTone, string> = {
  success: 'Thành công',
  error: 'Lỗi',
  info: 'Thông tin',
  warning: 'Cảnh báo',
};

const toneIcon: Record<ToastTone, ReactNode> = {
  success: <CheckCircle2 className="size-4 shrink-0" aria-hidden />,
  error: <OctagonAlert className="size-4 shrink-0" aria-hidden />,
  info: <Info className="size-4 shrink-0" aria-hidden />,
  warning: <AlertTriangle className="size-4 shrink-0" aria-hidden />,
};

let toastSeq = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const toast = useCallback((input: ToastInput) => {
    const id = `toast-${++toastSeq}`;
    setItems((prev) => [...prev, { ...input, id, open: true, tone: input.tone ?? 'info' }]);
  }, []);

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider swipeDirection="right" duration={4000}>
        {children}
        {items.map((item) => {
          const tone = item.tone ?? 'info';
          return (
            <ToastPrimitive.Root
              key={item.id}
              open={item.open}
              duration={item.duration}
              onOpenChange={(open) => {
                if (!open) {
                  setItems((prev) => prev.filter((t) => t.id !== item.id));
                }
              }}
              className={cn(
                'flex w-[min(100vw-2rem,22rem)] items-start gap-3 rounded-control border px-3 py-3 shadow-lg transition-all duration-200',
                'data-[state=open]:animate-in data-[state=open]:slide-in-from-top-3 sm:data-[state=open]:slide-in-from-right-full',
                'data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full',
                toneClass[tone],
              )}
            >
              <span className="mt-0.5">{toneIcon[tone]}</span>
              <div className="min-w-0 flex-1">
                <span className="sr-only">{toneLabel[tone]}. </span>
                {item.title ? (
                  <ToastPrimitive.Title className="text-body font-semibold">
                    {item.title}
                  </ToastPrimitive.Title>
                ) : null}
                <ToastPrimitive.Description className="text-caption">
                  {item.message}
                </ToastPrimitive.Description>
              </div>
              <ToastPrimitive.Close asChild>
                <IconButton label="Đóng thông báo" icon={<X />} size="md" tooltip={false} />
              </ToastPrimitive.Close>
            </ToastPrimitive.Root>
          );
        })}
        <ToastPrimitive.Viewport
          className={cn(
            'fixed right-3 top-4 z-[var(--z-toast)] flex max-h-[min(80vh,36rem)] w-[min(100vw-1.5rem,22rem)] flex-col gap-2.5 outline-none',
            'sm:top-5 sm:right-5',
          )}
        />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}
